import { lazy, Suspense } from "react";
import { Flex } from "@chakra-ui/react";
import { Navigate, Route, Routes } from "react-router";

import { useAbilities } from "./app/access-control";

const LoginPage = lazy(() =>
  import("@pages/login").then((module) => ({ default: module.LoginPage })),
);
const SignUpPage = lazy(() =>
  import("@pages/signup").then((module) => ({ default: module.SignUpPage })),
);
const AcceptInvitationPage = lazy(() =>
  import("@pages/accept-invitation").then((module) => ({
    default: module.AcceptInvitationPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import("@pages/verify-email").then((module) => ({
    default: module.VerifyEmailPage,
  })),
);
const EmailConfirmationWaitingRoom = lazy(() =>
  import("@pages/email-confirmation-waiting-room").then((module) => ({
    default: module.EmailConfirmationWaitingRoom,
  })),
);
const ProductsPage = lazy(() =>
  import("@pages/products").then((module) => ({
    default: module.ProductsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@pages/settings").then((module) => ({
    default: module.SettingsPage,
  })),
);
const PendingOrdersPage = lazy(() =>
  import("@pages/pending-orders").then((module) => ({
    default: module.PendingOrdersPage,
  })),
);
const DetailedOrderingPage = lazy(() =>
  import("@pages/detailed-ordering").then((module) => ({
    default: module.DetailedOrderingPage,
  })),
);
const OrdersPage = lazy(() =>
  import("@pages/orders").then((module) => ({ default: module.OrdersPage })),
);
const StaffPage = lazy(() =>
  import("@pages/staff").then((module) => ({ default: module.StaffPage })),
);
const ReceivingsPage = lazy(() =>
  import("@pages/receivings").then((module) => ({
    default: module.ReceivingsPage,
  })),
);
import { OutletLayout } from "@shared/ui/outlet-layout";
import { AdminRouteGuard } from "./app/routing/admin-route";
import { FetchingIndicator } from "@shared/ui/fetching-indicator";
import { ProtectedRouteGuard } from "./app/routing/protected-route";
import { LoadingStateScreen } from "@shared/ui/loading-state-screen";
import { LoggedOutOnlyRouteGuard } from "./app/routing/logged-out-only-route";
import { useAuthenticationContext } from "@app/auth/use-authentication-context";
const pageFallback = <LoadingStateScreen w="full" h="95vh" />;

const UsersAdministrationPage = lazy(() =>
  import("@pages/users-administration").then((module) => ({
    default: module.UsersAdministrationPage,
  })),
);

const CompaniesAdministrationPage = lazy(() =>
  import("@pages/companies-administration").then((module) => ({
    default: module.CompaniesAdministrationPage,
  })),
);

const EventLogPage = lazy(() =>
  import("@pages/event-log").then((module) => ({
    default: module.EventLogPage,
  })),
);

const IndexRedirector = () => {
  const { user, loggedIn } = useAuthenticationContext();

  return loggedIn && user?.id ? (
    <Navigate
      to={user?.isSuperuser ? "/admin/users" : "/account/detailed-ordering"}
    />
  ) : (
    <Navigate to="/login" />
  );
};

const RedirectToDetailedOrdering = () => (
  <Navigate to="/account/detailed-ordering" />
);
const RedirectToSettings = () => <Navigate to="/account/settings" />;
const RedirectToStaff = () => <Navigate to="/company/staff" />;

export const CustomerApplication = () => {
  const ability = useAbilities();
  const { activeCompany } = useAuthenticationContext();

  return (
    <>
      <Flex w="full" h="100vh" flexDir="column" overflowY="hidden">
        <FetchingIndicator />

        <Routes>
          <Route
            path="/login"
            element={
              <LoggedOutOnlyRouteGuard>
                <Suspense fallback={pageFallback}>
                  <LoginPage />
                </Suspense>
              </LoggedOutOnlyRouteGuard>
            }
          />
          <Route
            path="/signup"
            element={
              <LoggedOutOnlyRouteGuard>
                <Suspense fallback={pageFallback}>
                  <SignUpPage />
                </Suspense>
              </LoggedOutOnlyRouteGuard>
            }
          />
          <Route
            path="/accept-invitation"
            element={
              <Suspense fallback={pageFallback}>
                <AcceptInvitationPage />
              </Suspense>
            }
          />
          <Route
            path="/verify-email"
            element={
              <Suspense fallback={pageFallback}>
                <VerifyEmailPage />
              </Suspense>
            }
          />

          <Route
            path="/email-verification-waiting-room"
            element={
              <Suspense fallback={pageFallback}>
                <EmailConfirmationWaitingRoom />
              </Suspense>
            }
          />

          {/* Administration section routes */}
          <Route
            path="/admin"
            element={
              <AdminRouteGuard>
                <OutletLayout />
              </AdminRouteGuard>
            }
          >
            <Route
              index
              path="/admin/users"
              element={
                <Suspense fallback={pageFallback}>
                  <UsersAdministrationPage />
                </Suspense>
              }
            />
            <Route
              path="/admin/companies"
              element={
                <Suspense fallback={pageFallback}>
                  <CompaniesAdministrationPage />
                </Suspense>
              }
            />
            <Route
              path="/admin/event-log"
              element={
                <Suspense fallback={pageFallback}>
                  <EventLogPage />
                </Suspense>
              }
            />
          </Route>

          <Route
            path="/account"
            element={
              <ProtectedRouteGuard>
                <OutletLayout />
              </ProtectedRouteGuard>
            }
          >
            <Route
              index
              path="/account"
              element={<RedirectToDetailedOrdering />}
            />
            <Route
              path="/account/pending-orders"
              element={
                <Suspense fallback={pageFallback}>
                  <PendingOrdersPage />
                </Suspense>
              }
            />
            <Route
              path="/account/stock-management"
              element={
                <Suspense fallback={pageFallback}>
                  <ProductsPage />
                </Suspense>
              }
            />
            <Route
              path="/account/detailed-ordering"
              element={
                <Suspense fallback={pageFallback}>
                  <DetailedOrderingPage />
                </Suspense>
              }
            />
            <Route
              path="/account/settings"
              element={
                <Suspense fallback={pageFallback}>
                  <SettingsPage />
                </Suspense>
              }
            />
            <Route
              path="/account/receivings"
              element={
                activeCompany?.enableAdvancedStock ? (
                  <Suspense fallback={pageFallback}>
                    <ReceivingsPage />
                  </Suspense>
                ) : (
                  <RedirectToDetailedOrdering />
                )
              }
            />
            <Route
              path="/account/recently-restocked"
              element={<Navigate to="/account/receivings" replace />}
            />

            <Route
              path="/account/orders"
              element={
                ability.can("navigate", "my-orders-page") ? (
                  <Suspense fallback={pageFallback}>
                    <OrdersPage />
                  </Suspense>
                ) : (
                  <RedirectToSettings />
                )
              }
            />
          </Route>

          <Route
            element={
              <ProtectedRouteGuard>
                <OutletLayout />
              </ProtectedRouteGuard>
            }
          >
            <Route
              path="/event-log"
              element={
                <Suspense fallback={pageFallback}>
                  <EventLogPage />
                </Suspense>
              }
            />
          </Route>

          {ability.can("navigate", "staff-page") && (
            <Route
              path="/company"
              element={
                <ProtectedRouteGuard>
                  <OutletLayout />
                </ProtectedRouteGuard>
              }
            >
              <Route index path="/company" element={<RedirectToStaff />} />
              <Route
                path="/company/staff"
                element={
                  <Suspense fallback={pageFallback}>
                    <StaffPage />
                  </Suspense>
                }
              />
            </Route>
          )}

          <Route
            path="/company"
            element={
              <ProtectedRouteGuard>
                <OutletLayout />
              </ProtectedRouteGuard>
            }
          >
            <Route index path="/company" element={<RedirectToStaff />} />
            <Route
              path="/company/staff"
              element={
                ability.can("navigate", "staff-page") ? (
                  <Suspense fallback={pageFallback}>
                    <StaffPage />
                  </Suspense>
                ) : (
                  <Navigate to="/account" />
                )
              }
            />
          </Route>
          <Route index element={<IndexRedirector />} />
        </Routes>
      </Flex>
    </>
  );
};
